import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { Select } from '@picsio/ui';

import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import styles from './styles.module.scss';

const BASE_STATUSES = [
  {
    id: 'DRAFT',
    value: 'Draft',
  },
  {
    id: 'ACTIVE',
    value: 'Active',
  },
  {
    id: 'UNLISTED',
    value: 'Unlisted',
  },
];

const ARCHIVED_STATUS = {
  id: 'ARCHIVED',
  value: 'Archived',
};

export const MIXED_STATUS_ID = 'MIXED';

const ProductStatusSelect = ({
  productStatus,
  disabled,
  readOnly,
  readOnlyTooltip,
  includeArchived,
  mixedValuesLabel,
  menuProps,
  onChange,
}) => {
  const statuses = useMemo(() => {
    const list = includeArchived ? [...BASE_STATUSES, ARCHIVED_STATUS] : BASE_STATUSES;
    if (!mixedValuesLabel) return list;
    return [{ id: MIXED_STATUS_ID, value: mixedValuesLabel }, ...list];
  }, [includeArchived, mixedValuesLabel]);
  const selectValue = useMemo(
    () => (productStatus || 'DRAFT').toUpperCase(),
    [productStatus],
  );

  const statusLabel = useMemo(() => {
    const option = statuses.find(({ id }) => id === selectValue);

    return option?.value || selectValue.charAt(0) + selectValue.slice(1).toLowerCase();
  }, [selectValue, statuses]);

  const statusToneClassName = useMemo(() => {
    const normalizedStatus = (productStatus || '').toUpperCase();

    if (normalizedStatus === MIXED_STATUS_ID) return styles.statusArchived;
    if (normalizedStatus === 'ACTIVE') return styles.statusActive;
    if (normalizedStatus === 'ARCHIVED') return styles.statusArchived;
    if (normalizedStatus === 'UNLISTED') return styles.statusUnlisted;

    return styles.statusDraft;
  }, [productStatus]);

  const handleChangeStatus = useCallback((event) => {
    if (readOnly) return;
    const nextStatus = event.target.value;
    if (nextStatus === MIXED_STATUS_ID) return;
    onChange(nextStatus);
  }, [readOnly, onChange]);

  const renderStatusValue = useCallback((id) => {
    const option = statuses.find((item) => item.id === id);

    return option?.value || id;
  }, [statuses]);

  return (
    <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
      <div className={cn(styles.statusSelectWrap, statusToneClassName, {
        [styles.statusMixed]: selectValue === MIXED_STATUS_ID,
      })}
      >
        <If condition={readOnly}>
          <div className={styles.statusReadOnlyLabel}>
            {statusLabel}
          </div>
        </If>
        <If condition={!readOnly}>
          <Select
            value={selectValue}
            options={statuses}
            dataId="id"
            dataValue="value"
            disabled={disabled}
            renderValue={renderStatusValue}
            MenuProps={menuProps}
            onChange={handleChangeStatus}
          />
        </If>
      </div>
    </ReadOnlyFieldTooltip>
  );
};

ProductStatusSelect.propTypes = {
  productStatus: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  includeArchived: PropTypes.bool,
  mixedValuesLabel: PropTypes.string,
  menuProps: PropTypes.objectOf(PropTypes.any),
  onChange: PropTypes.func,
};

ProductStatusSelect.defaultProps = {
  productStatus: 'draft',
  disabled: false,
  readOnly: false,
  readOnlyTooltip: null,
  includeArchived: false,
  mixedValuesLabel: null,
  menuProps: undefined,
  onChange: () => {},
};

export default ProductStatusSelect;
